import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Keyboard,
  Platform,
  View,
  TouchableWithoutFeedback,
  LayoutChangeEvent
} from 'react-native';
import { TextInputMask, TextInputMaskProps } from 'react-native-masked-text';
import styled from 'styled-components/native';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';

const FormNumberFieldView = styled.View`
  width: 100%;
`;

const StyledContainer = styled.View<{
  hasHelperText?: boolean;
  minHeight?: number;
  error?: boolean;
  hasPrefixSuffix: boolean;
}>`
  align-items: center;
  flex-direction: row;
  width: 100%;
  height: 64px;
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: 8px 16px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
`;

const StyledTextInputMask = styled(TextInputMask)<{
  hasHelperText?: boolean;
  minHeight?: number;
  error?: boolean;
  empty?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  color: #000;
  /* This is a work around for Android to avoid getting text cropped as you type */
  ${Platform.OS === 'android'
    ? 'font-size: 14.5px; font-weight: normal; letter-spacing: -0.3px;'
    : 'font-size: 16px;'}
  min-width: 0;
  max-width: 100%;
  flex-shrink: 1;
` as React.FC<
  TextInputMaskProps & {
    hasHelperText?: boolean;
    minHeight?: number;
    ref?: any;
    error?: boolean;
    empty?: boolean;
  }
>;

const StyledTextInput = styled.TextInput<{
  hasHelperText?: boolean;
  minHeight?: number;
  error?: boolean;
  empty?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  /* color: #B3B3B3 */
  /* This is a work around for Android to avoid getting text cropped as you type */
  ${Platform.OS === 'android'
    ? 'font-size: 14.5px; font-weight: normal; letter-spacing: -0.3px;'
    : 'font-size: 16px;'}
  min-width: 1px;
  max-width: 100%;
  flex-shrink: 1;
` as React.FC<
  TextInputMaskProps & {
    hasHelperText?: boolean;
    minHeight?: number;
    ref?: any;
    error?: boolean;
    empty?: boolean;
  }
>;

const StyledText = styled.Text`
  font-family: 'DMSans-Bold';
  ${Platform.OS === 'android'
    ? 'font-size: 15px; font-weight: normal; letter-spacing: -0.3px;'
    : 'font-size: 16px;'}
`;

const ErrorText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.error};
  margin: 4px 4px 12px 4px;
`;

const thousandFormatter = (num: string) => {
  const THOUSAND_FORMATTER = new RegExp(/\B(?=(\d{3})+(?!\d))/g);
  if (!num) return;
  const number = num.replace(/^0+/, '');
  let leadingZeros = '';
  if (number.length !== num.length) {
    leadingZeros = '0'.repeat(num.length - number.length);
  }
  const numberSplitter = '.';
  if (number.includes(numberSplitter)) {
    const numberToFormat = number.split(numberSplitter);
    return (
      leadingZeros +
      [numberToFormat[0].replace(THOUSAND_FORMATTER, ','), numberToFormat[1]]
        .join(numberSplitter)
        .trim()
    );
  }
  return leadingZeros + number.replace(THOUSAND_FORMATTER, ',');
};

export const FormNumberField = <T extends object>({
  element,
  edit,
  disabled
}: FormElementProps<T, FormNumberConfig>) => {
  const { entitySchema, formState, formValues, setFormValue } =
    useDynamicFormContext<T, FormNumberConfig>();
  const key = (element.dataSource as keyof T) || element.key;
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [value, setValue] = useState((formValues[key] as any) || '');
  const previousValueRef = useRef(value);
  const [validateInputs, setValidateInputs] = useState(false);
  // refresh value if formValues change outside of the component
  useEffect(() => {
    if (
      !focused &&
      typeof formValues[key] !== 'undefined' &&
      previousValueRef.current !== formValues[key]
    ) {
      setValue(`${formValues[key] ?? ''}`);
      previousValueRef.current = formValues[key];
    }
  }, [focused, formValues, key, value]);
  const {
    placeholder,
    prefix,
    suffix,
    format,
    phoneNumber,
    creditcard,
    allowLeadingZero,
    mask,
    length,
    min,
    max,
    thousandSeparator,
    currency,
    dataType
  } = element.config || {};
  const textInputRef = useRef<any>();

  /* This is a work around for web to make the text input width dynamic */
  const [textWidth, setTextWidth] = useState(1);

  const Input =
    format || phoneNumber || creditcard ? StyledTextInputMask : StyledTextInput;

  const { precision, scale, columnType } = useMemo(() => {
    const column = entitySchema?.columns.find(
      c => c.name === element.dataSource
    );
    let precision = null,
      scale = null;
    if (column?.type === 'numeric') {
      precision = column.precision;
      scale = column.scale;
    }
    return { precision, scale, columnType: column?.type };
  }, [element.dataSource, entitySchema?.columns]);

  const validNumeric = useCallback(
    (input: number) => {
      if (precision && scale) {
        const format = (num: number) => num.toFixed(scale);
        const value = +format(input);
        if (isNaN(value) || value >= Math.pow(10, precision - scale)) {
          setErrorMessage(
            `Rounded value must not be greater than ${Math.pow(
              10,
              precision - scale
            )}`
          );
          setError(true);
          setFormValue(key, null as any, element);
          return false;
        }
      }
      setError(false);
      return true;
    },
    [element, key, precision, scale, setFormValue]
  );

  function handleChange(input: any) {
    if (input === null) return;
    const value = input.toString();
    if (min !== undefined && +value < min) {
      setErrorMessage(`Input value must not be less than ${min}`);
      setError(true);
      setFormValue(key, null as any, element);
    } else if (max !== undefined && +value > max) {
      setErrorMessage(`Input value must not be greater than ${max}`);
      setError(true);
      setFormValue(key, null as any, element);
    } else {
      setError(false);
    }
  }
  return (
    <View>
      <FormElementQuestion element={element} />
      <FormNumberFieldView>
        <TouchableWithoutFeedback
          onPress={() => {
            if (format || phoneNumber || creditcard) {
              textInputRef?.current?._inputElement.focus();
            } else {
              textInputRef?.current?.focus();
            }
          }}
        >
          <StyledContainer hasPrefixSuffix={Boolean(prefix || suffix)}>
            {prefix && (value as string)?.length > 0 && (
              <StyledText accessible={false}>{prefix}</StyledText>
            )}
            {Platform.OS === 'web' && (
              <StyledText
                onLayout={(event: LayoutChangeEvent) => {
                  let width = event.nativeEvent.layout.width;
                  if (width > 0) width += 10;
                  setTextWidth(width);
                }}
                style={{ position: 'absolute', opacity: 0, zIndex: -1 }}
              >
                {value}
              </StyledText>
            )}
            <Input
              style={{
                width:
                  Platform.OS === 'web'
                    ? value.length > 0
                      ? textWidth
                      : '100%'
                    : undefined
              }}
              ref={textInputRef}
              type={'custom'}
              options={{
                mask: phoneNumber
                  ? '(999) 999-9999'
                  : creditcard
                    ? '9999 9999 9999 9999'
                    : (format as string)?.replace(/#/g, '9')
              }}
              includeRawValueInChangeText={true}
              blurOnSubmit={true}
              value={`${value}`}
              empty={value !== 0 && !value}
              onChangeText={(text, rawText) => {
                if (
                  element.required &&
                  typeof text !== 'undefined' &&
                  text === ''
                ) {
                  setValue(text);
                  setFormValue(key, null as any, element);
                  return;
                }
                let newText = text;
                if (!format && !phoneNumber && !creditcard) {
                  if (text.length === 1 && text.match(/[^0-9]/g)) {
                    if (precision && text.match(/^[.]/)) {
                      setValue('0.');
                      setFormValue(key, 0 as any, element);
                    } else {
                      setValue('');
                      return;
                    }
                  }
                  if (text.match(/[^0-9.,]/g)) return;
                  if (!allowLeadingZero || thousandSeparator) {
                    const num = newText.replace(/,/g, '');
                    if (isNaN(+num)) return;
                    if (!allowLeadingZero) {
                      newText = num.replace(/^0+/, '0');
                      if (newText.match(/^0+[0-9]/)) {
                        newText = num.replace(/^0+/, '');
                      }
                    }
                    if (thousandSeparator) {
                      newText =
                        thousandFormatter(newText.replace(/,/g, '')) || '';
                    }
                  } else {
                    if (isNaN(+newText)) return;
                  }
                  if (columnType === 'integer') {
                    newText = newText.replace(/[.]/g, '');
                  }
                }
                setValue(newText);
                const raw = rawText || text;
                if (
                  dataType === 'string' ||
                  allowLeadingZero ||
                  creditcard ||
                  phoneNumber
                ) {
                  setFormValue(key, raw as any, element);
                  previousValueRef.current = raw;
                } else {
                  const num = raw.replace(/[^0-9.]/g, '');
                  if (precision && scale && !validNumeric(+num)) return;
                  setFormValue(key, +num as any, element);
                  previousValueRef.current = +num;
                  if (validateInputs) handleChange(num);
                }
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onBlur={event => {
                setFocused(false);
                setValidateInputs(true);
                handleChange(value);
              }}
              onSubmitEditing={() => {
                setFocused(false);
                setValidateInputs(true);
                handleChange(value);
                Keyboard.dismiss();
              }}
              placeholder={placeholder}
              placeholderTextColor={Colors.placeholderColor}
              keyboardType={columnType === 'integer' ? 'number-pad' : 'numeric'}
            />
            <StyledText accessible={false}>
              {suffix && (value as string)?.length > 0 ? suffix : ''}
            </StyledText>
          </StyledContainer>
        </TouchableWithoutFeedback>
      </FormNumberFieldView>
      {error && <ErrorText>{errorMessage}</ErrorText>}
    </View>
  );
};

type FormatFunction = (value: number | string) => string;

export type FormNumberConfig = {
  placeholder?: string;
  dataType?: string;
  prefix?: string;
  suffix?: string;
  format?: string | FormatFunction;
  isString?: boolean;
  currency?: boolean;
  phoneNumber?: boolean;
  creditcard?: boolean;
  allowLeadingZero?: boolean;
  thousandSeparator?: boolean;
  mask?: string;
  length?: number;
  min?: number;
  max?: number;
  InputProps?: any;
};

export const FormNumberFieldPlugin: FormElementPlugin<any, FormNumberConfig> = {
  name: 'Number',
  type: 'NumberField',
  dataTypes: ['integer', 'numeric', 'text'],
  init: (type, _, previousElement) => ({
    type,
    dataType: 'number',
    title: previousElement?.title || 'Question',
    key: previousElement?.key || 'question'
  }),
  element: FormNumberField
};
